import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createElementVNode as _createElementVNode, withCtx as _withCtx, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, normalizeClass as _normalizeClass, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "col-lg-8 mx-auto" }
const _hoisted_2 = {
  class: "d-grid row-gap-4 mb-2",
  role: "list"
}
const _hoisted_3 = {
  key: 0,
  class: "alert alert-primary mt-2",
  role: "alert"
}
const _hoisted_4 = {
  key: 1,
  class: "form-check form-switch"
}
const _hoisted_5 = ["disabled"]
const _hoisted_6 = {
  class: "form-check-label ms-2 mb-4 fw-bold",
  for: "consentToTermsAndConditions"
}
const _hoisted_7 = ["href"]
const _hoisted_8 = { key: 2 }
const _hoisted_9 = { class: "steps-complete" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = { key: 0 }
const _hoisted_12 = ["href"]

import { ref, computed, onMounted, watch } from "vue";
import { app } from "../ts/app";
import { useStore } from "vuex";
import { workflow } from "@/guiide/ts/workflow/workflow";
import { WorkflowStep } from "@/guiide/ts/workflow/workflowStep";
import ConsentInfo, { emptyConsentInfo } from "../ts/consent/consentInfo";
import PermittedEntityType from "../ts/consent/permittedEntityType";
import { Consent } from "../ts/consent";
import { Notification as AppNotification } from "../ts/notifications/notification";
import SubSection from "@/guiide/components/common/SubSection.vue";
import Modal from "@/guiide/components/common/Modal.vue";
import ConsentCapture from "./consent/ConsentCapture.vue";
import Notification from "./common/Notification.vue";
import UserHomeCustomContentKey from "../ts/userhomeCustomContentKey";
import AonUserHome from "./userhome/AonUserHome.vue";
import NotificationConcern from "../ts/notifications/notificationConcern";
import LifeExpectancyBasis from "../ts/lifeExpectancyBasis";
import { hasConsented } from "@/guiide/ts/utilities";
import ConsentType from "@/guiide/ts/consent/consentType";
import ExternalAgency from "@/guiide/ts/consent/externalAgency";
import DynamicAdviceViewer from "@/guiide/components/common/DynamicAdviceViewer.vue";
import Result from "@/guiide/ts/result";
import { useUIContent } from "../ts/configuration/useUIContent";

type ViewModel = {
    canShowDashboardOption: boolean,
    dashboardTitle: string,
    dashboardText: string,
    dashboardAccessWarning: string,
    mainJourneyReferenceText: string,
    mainJourneyText: string[],
    showAdviceLink: boolean,
    completedSteps: string,
    totalSteps: string,
    showTermsOfUse: boolean,
    termsOfUseResource: string
    termsAndConditionsConsentToggleDisabled: boolean,
    termsAndConditionsConsent: boolean,
    disableContinue: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'UserHome',
  setup(__props) {

const store = useStore();
const { textArray } = useUIContent();
const workflowSteps = ref<Array<WorkflowStep>>(null);
const notifications = ref<Array<AppNotification>>(new Array<AppNotification>());
const consentModal = ref(null);
const modalIsOpen = ref(false);
const consentInfo = ref<ConsentInfo>(emptyConsentInfo);
const dashboardAccessWarning = ref("");
const submissionError = ref("");

onMounted(async () => {
  workflowSteps.value = await workflow.getSteps();
  notifications.value = await app.getNotifications(NotificationConcern.General);
});

watch(
    () => [ store.state.configuration, store.state.retiree ],
    ([config, retiree]) => {
        if (config == null || retiree == null || modalIsOpen.value) {
            return;
        }

        if (retiree.missingConsents.length > 0) {
            const missingConsent: Consent = retiree.missingConsents[0];
            modalIsOpen.value = true;
            consentInfo.value = new ConsentInfo(
                config.companyFullName, 
                config.companyAbbrevName, 
                PermittedEntityType.ExternalAgency,
                missingConsent.permittedEntityId,
                missingConsent.type);
            consentModal.value.showModal();
        }
    }
)

const viewModel = computed<ViewModel>(() => {
    const config = store.state.configuration;
    const retiree = store.state.retiree;

    if (config == null || retiree == null) {
        return getEmptyViewModel();
    }

    const canShowDashboardOption = retiree.isRegistered && !retiree.mustConfirmRetired;

    let showAdviceLink = false;
    let mainJourneyText: string[];
    
    if (!retiree.hasFinishedDesign) {
        mainJourneyText = textArray("userhome.mainjourney.statement", "does-not-have-finished-design");
        showAdviceLink = true;
    } else if (!retiree.isRegistered) {
        mainJourneyText = textArray("userhome.mainjourney.statement", "not-registered");
    } else {
        mainJourneyText = textArray("userhome.mainjourney.statement", "registered");
    }

    const termsAndConditionsConsent = hasConsented(ExternalAgency.Aon, ConsentType.TermsAndConditionsAccepted);

    return {
        canShowDashboardOption,
        dashboardTitle: config.userHomeTrackIntroTitle,
        dashboardText: config.userHomeTrackIntroText,
        dashboardAccessWarning: dashboardAccessWarning.value,
        mainJourneyReferenceText: config.mainJourneyReferenceText,
        mainJourneyText,
        showAdviceLink,
        completedSteps: config.workflow.stepsCompleted.toString(),
        totalSteps: workflowSteps.value?.length.toString() ?? "",
        showTermsOfUse: config.showTermsOfUse,
        termsOfUseResource: config.termsOfUseResource,
        termsAndConditionsConsentToggleDisabled: store.state.transactionOngoing,
        termsAndConditionsConsent: termsAndConditionsConsent,
        disableContinue: !termsAndConditionsConsent
    }
});

const customContentKey = computed<UserHomeCustomContentKey>(() => store.state.configuration?.userHomeCustomContentKey ?? UserHomeCustomContentKey.None);

const showNotifications = computed<boolean>(() => notifications.value.length > 0);

function onDashboardClicked() {
    const config = store.state.configuration;
    const retiree = store.state.retiree;

    if (config == null || retiree == null) {
        return;
    }

    if (config.lifeExpectancyBasis == LifeExpectancyBasis.Survival && retiree.survivalPercentile == null) {
        dashboardAccessWarning.value = "Please enter the main journey to examine any effect of the new method of working out your plan duration.";
        return;
    }

    window.location.href = `/${config.nav.track.resource}`;
}

function onMainJourneyClicked() {
    window.location.href = `/home/startdesign`;
}

function onMissingConsentsCaptured() {
    consentModal.value.closeModal();
    modalIsOpen.value = false;
}

function getEmptyViewModel() {
    return {
        canShowDashboardOption: false,
        dashboardTitle: "",
        dashboardText: "",
        dashboardAccessWarning: "",
        mainJourneyReferenceText: "",
        mainJourneyText: [],
        showAdviceLink: false,
        completedSteps: "",
        totalSteps: "",
        showTermsOfUse: false,
        termsOfUseResource: "",
        termsAndConditionsConsentToggleDisabled: true,
        termsAndConditionsConsent: false,
        disableContinue: true
    };
}

async function handleTermsAndConditionsConsentToggle() {
    viewModel.value.disableContinue = true;

    let consented = viewModel.value.termsAndConditionsConsent;

    let toggleConsentResult: Result;
    if (consented) {
        toggleConsentResult = await app.grantConsent(PermittedEntityType.ExternalAgency, ExternalAgency.Aon, ConsentType.TermsAndConditionsAccepted, true);
    } else {
        toggleConsentResult = await app.revokeConsent(PermittedEntityType.ExternalAgency, ExternalAgency.Aon, ConsentType.TermsAndConditionsAccepted);
    }

    if (toggleConsentResult.successful) {
        viewModel.value.disableContinue = !consented;
    } else {
        submissionError.value = "Failed to save change.";
    }
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      (showNotifications.value)
        ? (_openBlock(), _createBlock(SubSection, {
            key: 0,
            title: "Messages",
            "show-help": false
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_2, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(notifications.value, (notification, i) => {
                  return (_openBlock(), _createBlock(Notification, {
                    key: i,
                    notification: notification
                  }, null, 8, ["notification"]))
                }), 128))
              ])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (viewModel.value.canShowDashboardOption)
        ? (_openBlock(), _createBlock(SubSection, {
            key: 1,
            title: viewModel.value.dashboardTitle,
            "show-help": false
          }, {
            default: _withCtx(() => [
              _createElementVNode("p", null, _toDisplayString(viewModel.value.dashboardText), 1),
              (viewModel.value.dashboardAccessWarning)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _cache[1] || (_cache[1] = _createElementVNode("i", { class: "bi bi-exclamation-circle-fill" }, null, -1)),
                    _createTextVNode(" " + _toDisplayString(viewModel.value.dashboardAccessWarning), 1)
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("button", {
                class: "btn btn-primary",
                onClick: onDashboardClicked
              }, "Dashboard")
            ]),
            _: 1
          }, 8, ["title"]))
        : _createCommentVNode("", true),
      _createVNode(SubSection, {
        title: viewModel.value.mainJourneyReferenceText,
        "show-help": false
      }, {
        default: _withCtx(() => [
          (customContentKey.value != _unref(UserHomeCustomContentKey).None)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                (customContentKey.value == _unref(UserHomeCustomContentKey).Aon)
                  ? (_openBlock(), _createBlock(AonUserHome, { key: 0 }))
                  : _createCommentVNode("", true)
              ], 64))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(viewModel.value.mainJourneyText, (introText, i) => {
            return (_openBlock(), _createElementBlock("p", {
              key: i,
              class: _normalizeClass({ 'mt-0': i === 0 })
            }, _toDisplayString(introText), 3))
          }), 128)),
          (customContentKey.value == _unref(UserHomeCustomContentKey).Aon)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _withDirectives(_createElementVNode("input", {
                  id: "consentToTermsAndConditions",
                  type: "checkbox",
                  class: "form-check-input",
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((viewModel.value.termsAndConditionsConsent) = $event)),
                  role: "switch",
                  onChange: handleTermsAndConditionsConsentToggle,
                  disabled: viewModel.value.termsAndConditionsConsentToggleDisabled
                }, null, 40, _hoisted_5), [
                  [_vModelCheckbox, viewModel.value.termsAndConditionsConsent]
                ]),
                _createElementVNode("label", _hoisted_6, [
                  _cache[2] || (_cache[2] = _createTextVNode(" Please read and agree to our ")),
                  _createElementVNode("a", {
                    target: "_blank",
                    href: viewModel.value.termsOfUseResource
                  }, "terms of use", 8, _hoisted_7),
                  _cache[3] || (_cache[3] = _createTextVNode(" before continuing. "))
                ])
              ]))
            : _createCommentVNode("", true),
          (viewModel.value.showAdviceLink)
            ? (_openBlock(), _createElementBlock("p", _hoisted_8, _cache[4] || (_cache[4] = [
                _createElementVNode("a", {
                  href: "/howtocompletedesign",
                  target: "_blank"
                }, "Find out about building a model of your retirement.", -1)
              ])))
            : _createCommentVNode("", true),
          _createElementVNode("p", _hoisted_9, "Steps complete: " + _toDisplayString(viewModel.value.completedSteps) + "/" + _toDisplayString(viewModel.value.totalSteps), 1),
          _createVNode(DynamicAdviceViewer, {
            message: submissionError.value,
            alertContext: "danger"
          }, null, 8, ["message"]),
          _createElementVNode("button", {
            class: "btn btn-primary",
            onClick: onMainJourneyClicked,
            disabled: customContentKey.value == _unref(UserHomeCustomContentKey).Aon && viewModel.value.disableContinue
          }, "Continue", 8, _hoisted_10)
        ]),
        _: 1
      }, 8, ["title"]),
      (customContentKey.value == _unref(UserHomeCustomContentKey).None)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
            (viewModel.value.showTermsOfUse)
              ? (_openBlock(), _createElementBlock("p", _hoisted_11, [
                  _createElementVNode("small", null, [
                    _cache[5] || (_cache[5] = _createTextVNode("By continuing, you are agreeing to our ")),
                    _createElementVNode("a", {
                      target: "_blank",
                      href: viewModel.value.termsOfUseResource
                    }, "terms of use", 8, _hoisted_12),
                    _cache[6] || (_cache[6] = _createTextVNode("."))
                  ])
                ]))
              : _createCommentVNode("", true)
          ], 64))
        : _createCommentVNode("", true)
    ]),
    _createVNode(Modal, {
      title: "Data sharing consent",
      ref_key: "consentModal",
      ref: consentModal,
      idKey: "consentModalKey"
    }, {
      default: _withCtx(() => [
        _createVNode(ConsentCapture, {
          info: consentInfo.value,
          onFinished: onMissingConsentsCaptured
        }, null, 8, ["info"])
      ]),
      _: 1
    }, 512)
  ], 64))
}
}

})